import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { RotateCw, Check, Lock } from "lucide-react";

/**
 * OrientationControlButton
 *
 * Controllo dell'orientamento del TIME Watch (Hub, PMV, Utility, Orologio).
 *
 * Comportamento (rev. definitiva):
 *  - Automatico  → unlock (se disponibile) + esce dal fullscreen (se attivato da noi)
 *                  Nessun blocco software, nessuna trasformazione CSS.
 *  - Verticale   → richiesta fullscreen SU document.documentElement (gesto utente)
 *                  → poi screen.orientation.lock('portrait').
 *  - Orizzontale → richiesta fullscreen SU document.documentElement (gesto utente)
 *                  → poi screen.orientation.lock('landscape').
 *
 *  ✔ Il fullscreen non viene MAI attivato al caricamento pagina.
 *  ✔ La preferenza salvata (localStorage `timeWatchOrientationPref`)
 *    viene solo mostrata come "ricordata"; per riattivare portrait/landscape
 *    l'utente deve toccare di nuovo l'opzione (fullscreen + orientation.lock
 *    richiedono un gesto esplicito).
 *  ✔ Se l'utente esce manualmente dal fullscreen, lo stato di lock viene
 *    reso incoerente-safe: `lockActive` scende a false; se preferito, si
 *    ripristina "automatico" per non mostrare un blocco che non esiste più.
 *  ✔ Su dispositivi/browser che non supportano orientation.lock (tipicamente
 *    iOS Safari), Verticale e Orizzontale restano visibili ma disabilitate,
 *    con messaggio: "Blocco dell'orientamento non supportato su questo
 *    dispositivo." Automatico resta sempre disponibile.
 */

const PREF_KEY = "timeWatchOrientationPref";

const OPTIONS = [
  { id: "auto",      label: "Automatico",  hint: "Segue il dispositivo", testId: "orientation-option-auto" },
  { id: "portrait",  label: "Verticale",   hint: "Blocca in portrait",   testId: "orientation-option-portrait" },
  { id: "landscape", label: "Orizzontale", hint: "Blocca in landscape",  testId: "orientation-option-landscape" },
];

function readPref() {
  try {
    const v = localStorage.getItem(PREF_KEY);
    if (v === "auto" || v === "portrait" || v === "landscape") return v;
  } catch { /* ignore */ }
  return "auto";
}

function writePref(v) {
  try { localStorage.setItem(PREF_KEY, v); } catch { /* ignore */ }
}

// ---------- Capability detection (safe SSR-guarded) ----------
function detectCapabilities() {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return { fullscreenSupported: false, orientationLockSupported: false };
  }
  const de = document.documentElement;
  const fullscreenSupported = !!(
    de.requestFullscreen ||
    de.webkitRequestFullscreen ||
    de.msRequestFullscreen
  );
  const so = window.screen && window.screen.orientation;
  const orientationLockSupported = !!(so && typeof so.lock === "function");
  return { fullscreenSupported, orientationLockSupported };
}

// ---------- Fullscreen helpers (cross-browser, silent) ----------
function isInFullscreen() {
  if (typeof document === "undefined") return false;
  return !!(
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.msFullscreenElement
  );
}

async function requestFullscreenOnRoot() {
  if (typeof document === "undefined") return false;
  const el = document.documentElement;
  const fn =
    el.requestFullscreen ||
    el.webkitRequestFullscreen ||
    el.msRequestFullscreen;
  if (!fn) return false;
  try {
    await fn.call(el);
    return true;
  } catch {
    return false;
  }
}

async function exitFullscreenSafe() {
  if (typeof document === "undefined") return;
  if (!isInFullscreen()) return;
  const fn =
    document.exitFullscreen ||
    document.webkitExitFullscreen ||
    document.msExitFullscreen;
  if (!fn) return;
  try {
    await fn.call(document);
  } catch { /* ignore */ }
}

// ---------- Orientation helpers (silent) ----------
async function tryLock(target) {
  try {
    const so = window.screen && window.screen.orientation;
    if (!so || typeof so.lock !== "function") return false;
    await so.lock(target);
    return true;
  } catch {
    return false;
  }
}

function tryUnlock() {
  try {
    const so = window.screen && window.screen.orientation;
    if (so && typeof so.unlock === "function") so.unlock();
  } catch { /* ignore */ }
}

export default function OrientationControlButton({ className = "" }) {
  const [pref, setPref] = useState(() => readPref());
  const [open, setOpen] = useState(false);
  // `lockActive` = c'è attualmente un lock+fullscreen richiesto da noi ed
  // effettivamente attivo. Serve a distinguere "modalità ricordata" da
  // "modalità realmente in vigore" (es. dopo un F11 dell'utente).
  const [lockActive, setLockActive] = useState(false);
  const [notice, setNotice] = useState(null); // messaggio breve, es. non supportato
  const rootRef = useRef(null);
  // Traccia se il fullscreen corrente è stato richiesto da questo controllo.
  const enteredFsFromUs = useRef(false);

  const caps = useMemo(() => detectCapabilities(), []);
  const canLock = caps.orientationLockSupported && caps.fullscreenSupported;

  // Al MOUNT non forziamo nulla:
  // - se pref === "auto", proviamo un unlock silenzioso (non richiede gesto)
  // - se pref è portrait/landscape, la ricordiamo ma non attiviamo lock/fs
  useEffect(() => {
    if (pref === "auto") tryUnlock();
    // NB: intenzionalmente niente auto-lock per portrait/landscape:
    // fullscreen + orientation.lock richiedono un gesto utente.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ascolta l'uscita dal fullscreen per rendere lo stato coerente:
  // se non siamo più in fullscreen e avevamo un lock attivo → lock non più valido.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const handler = () => {
      if (!isInFullscreen()) {
        // Uscita dal fullscreen: il lock browser-side decade automaticamente.
        if (lockActive) {
          setLockActive(false);
          // Torna a "auto" come stato coerente e persisti la scelta.
          setPref("auto");
          writePref("auto");
          tryUnlock();
        }
        enteredFsFromUs.current = false;
      }
    };
    document.addEventListener("fullscreenchange", handler);
    document.addEventListener("webkitfullscreenchange", handler);
    document.addEventListener("msfullscreenchange", handler);
    return () => {
      document.removeEventListener("fullscreenchange", handler);
      document.removeEventListener("webkitfullscreenchange", handler);
      document.removeEventListener("msfullscreenchange", handler);
    };
  }, [lockActive]);

  // Chiudi menu su click fuori
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown, { passive: true });
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [open]);

  // ESC chiude menu (nota: ESC nativo esce anche dal fullscreen — gestito sopra)
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Notice auto-dismiss
  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(null), 4500);
    return () => clearTimeout(t);
  }, [notice]);

  // === Handler selezione opzione (SEMPRE dentro un gesto utente) ===
  const handleSelect = useCallback(async (id) => {
    // 1) AUTOMATICO
    if (id === "auto") {
      tryUnlock();
      if (enteredFsFromUs.current) {
        await exitFullscreenSafe();
      }
      enteredFsFromUs.current = false;
      setLockActive(false);
      setPref("auto");
      writePref("auto");
      setOpen(false);
      return;
    }

    // 2) VERTICALE / ORIZZONTALE
    // Persistiamo comunque la preferenza (il ricordo dell'utente),
    // ma il lock effettivo dipende dal supporto del device.
    setPref(id);
    writePref(id);
    setOpen(false);

    if (!canLock) {
      setNotice("Blocco dell'orientamento non supportato su questo dispositivo.");
      setLockActive(false);
      return;
    }

    // Serve un gesto utente ATTIVO: siamo dentro l'onClick, quindi ok.
    // Richiedi fullscreen se non lo siamo già.
    let inFs = isInFullscreen();
    if (!inFs) {
      const ok = await requestFullscreenOnRoot();
      if (!ok) {
        setNotice("Blocco dell'orientamento non supportato su questo dispositivo.");
        setLockActive(false);
        return;
      }
      enteredFsFromUs.current = true;
      inFs = true;
    }

    const target = id === "portrait" ? "portrait" : "landscape";
    const locked = await tryLock(target);
    if (!locked) {
      setNotice("Blocco dell'orientamento non supportato su questo dispositivo.");
      setLockActive(false);
      return;
    }
    setLockActive(true);
  }, [canLock]);

  const rememberedLabel = OPTIONS.find((o) => o.id === pref)?.label;

  return (
    <div
      ref={rootRef}
      data-testid="orientation-control"
      className={`fixed bottom-[168px] sm:bottom-24 left-4 sm:left-6 z-50 pointer-events-none ${className}`}
      style={{
        paddingLeft: "env(safe-area-inset-left, 0px)",
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
      }}
    >
      {/* Notice contestuale (auto-dismiss) */}
      {notice && (
        <div
          data-testid="orientation-notice"
          role="status"
          className="pointer-events-auto absolute bottom-full left-0 mb-2 max-w-[240px] px-3 py-2 rounded-lg backdrop-blur-md bg-black/70 border border-amber-400/40 text-[10px] tracking-[0.15em] uppercase text-amber-200 font-mono leading-snug"
        >
          {notice}
        </div>
      )}

      {open && (
        <div
          data-testid="orientation-menu"
          role="menu"
          aria-label="Seleziona modalità di orientamento"
          className="pointer-events-auto absolute bottom-full left-0 mb-2 min-w-[220px] rounded-xl backdrop-blur-md bg-black/70 border border-white/15 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.8)] overflow-hidden"
        >
          {OPTIONS.map((opt) => {
            const active = pref === opt.id;
            // Portrait/Landscape sono "cliccabili" ma su device non supportati
            // producono un messaggio informativo (opzione visivamente disabilitata).
            const disabled = (opt.id !== "auto") && !canLock;
            const showLockActive = active && opt.id !== "auto" && lockActive;
            return (
              <button
                key={opt.id}
                type="button"
                role="menuitemradio"
                aria-checked={active}
                aria-disabled={disabled}
                data-testid={opt.testId}
                data-active={active ? "true" : "false"}
                data-disabled={disabled ? "true" : "false"}
                onClick={() => handleSelect(opt.id)}
                className={`w-full flex items-center gap-3 px-3.5 py-2.5 text-left transition-colors ${
                  disabled
                    ? "text-white/25 cursor-not-allowed"
                    : active
                      ? "bg-emerald-400/10 text-emerald-300"
                      : "text-white/80 hover:bg-white/5 hover:text-white"
                }`}
              >
                <span
                  aria-hidden="true"
                  className={`w-4 h-4 flex items-center justify-center ${
                    active ? "opacity-100" : "opacity-0"
                  }`}
                >
                  <Check className="w-3.5 h-3.5" />
                </span>
                <span className="flex flex-col leading-tight flex-1">
                  <span className="text-[11px] tracking-[0.22em] uppercase font-mono">
                    {opt.label}
                  </span>
                  <span className="text-[10px] text-white/40 mt-0.5 normal-case tracking-normal">
                    {disabled ? "Non disponibile" : opt.hint}
                  </span>
                </span>
                {showLockActive && (
                  <Lock className="w-3 h-3 text-emerald-400" aria-hidden="true" />
                )}
              </button>
            );
          })}
          {/* Footer informativo quando il device non supporta il lock */}
          {!canLock && (
            <div className="px-3.5 py-2 border-t border-white/10 text-[9px] tracking-[0.12em] uppercase text-white/40 font-mono leading-snug">
              Blocco dell'orientamento non supportato su questo dispositivo.
            </div>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="orientation-toggle-button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Controllo orientamento — modalità corrente: ${rememberedLabel}`}
        className={`pointer-events-auto relative w-11 h-11 rounded-full backdrop-blur-md bg-black/50 border transition-all duration-300 flex items-center justify-center cursor-pointer ${
          open
            ? "border-emerald-400/60 text-emerald-300"
            : "border-white/15 text-white/70 hover:text-white hover:border-white/40"
        }`}
      >
        <RotateCw className="w-4 h-4" />
        {/* Piccolo indicatore quando c'è un lock attivo */}
        {lockActive && (
          <span
            aria-hidden="true"
            className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_6px_rgba(16,185,129,0.9)]"
          />
        )}
      </button>
    </div>
  );
}
